import {Box, Card, Grid, Section, SectionIntro, Statistic} from '@primer/react-brand/esm'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import {BrandedDivider} from '../../../components/BrandedDivider'
import {FlexSuiteLargeCards} from '../../../components/FlexSuiteLargeCards'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {clsx} from 'clsx'
import {FlexSuiteTestimonialBanner, FlexSuiteTestimonialQuote} from '../../../components/FlexSuiteTestimonial'
import baseStyles from '../../../styles/base.module.css'
import {renderWithoutParagraphs} from '../../../utils'
import type {OutroSectionProps} from './types'

export function OutroSection({
  settings,
  outroLargeCards,
  outroTestimonial,
  outroTestimonialLink,
  outroTestimonialLogo,
  outroTestimonialQuote,
  outroTestimonialImage,
  outroTestimonialLabel,
  outroTestimonialName,
  outroTestimonialTitle,
  outroStats,
  outroCustomerStories,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroBrandedDivider,
  outroResourcesHeading,
  outroResources,
}: OutroSectionProps) {
  const brand = settings?.fields.brand

  const hasLargeCards = Boolean(outroLargeCards?.length)
  const hasQuoteTestimonial = outroTestimonial === 'Quote' && Boolean(outroTestimonialQuote)
  const hasBrandBannerTestimonial = outroTestimonial === 'Brand Banner' && Boolean(outroTestimonialQuote)
  const hasStats = Boolean(outroStats?.length)
  const hasCustomerStories = Boolean(outroCustomerStories?.length)
  const hasResources = Boolean(outroResources?.length)
  const hasCtaBanner = Boolean(
    outroCtaHeading || outroCtaDescription || outroCtaOne?.fields.href || outroCtaTwo?.fields.href,
  )
  const hasBrandedDivider = Boolean(outroBrandedDivider && brand === 'Security')

  if (
    !hasLargeCards &&
    !hasQuoteTestimonial &&
    !hasBrandBannerTestimonial &&
    !hasStats &&
    !hasCustomerStories &&
    !hasCtaBanner &&
    !hasResources &&
    !hasBrandedDivider
  ) {
    return null
  }

  return (
    <Box marginBlockStart={48} className={baseStyles.greenBorderBottom}>
      <Section fullWidth paddingBlockStart="none" paddingBlockEnd="none">
        {hasLargeCards ? (
          <Box
            className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}
            paddingBlockStart={48}
            paddingBlockEnd={48}
          >
            <FlexSuiteLargeCards cards={outroLargeCards} />
          </Box>
        ) : null}

        {hasQuoteTestimonial ? (
          <Box paddingBlockStart="spacious" paddingBlockEnd="spacious">
            <Grid>
              <Grid.Column>
                <FlexSuiteTestimonialQuote
                  quote={outroTestimonialQuote}
                  name={outroTestimonialName}
                  title={outroTestimonialTitle}
                  link={outroTestimonialLink?.fields}
                  logo={outroTestimonialLogo}
                />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {hasBrandBannerTestimonial ? (
          <Box paddingBlockStart="spacious" paddingBlockEnd="spacious">
            {outroTestimonialLabel ? (
              <Box marginBlockEnd={16}>
                <SectionIntro align="center" fullWidth>
                  <SectionIntro.Label>{outroTestimonialLabel}</SectionIntro.Label>
                </SectionIntro>
              </Box>
            ) : null}
            <FlexSuiteTestimonialBanner
              quote={outroTestimonialQuote}
              link={outroTestimonialLink?.fields}
              logo={outroTestimonialLogo}
              image={outroTestimonialImage}
            />
          </Box>
        ) : null}

        {hasStats ? (
          <Box
            className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}
            paddingBlockStart={{narrow: 'spacious', regular: 64}}
            paddingBlockEnd={{narrow: 'spacious', regular: 64}}
          >
            <Grid>
              {outroStats?.map(stat => (
                <Grid.Column key={stat.sys.id} span={{medium: 4}}>
                  <Statistic>
                    <Statistic.Heading>{renderWithoutParagraphs(stat.fields.heading)}</Statistic.Heading>
                    <Statistic.Description>{renderWithoutParagraphs(stat.fields.description)}</Statistic.Description>
                  </Statistic>
                </Grid.Column>
              ))}
            </Grid>
          </Box>
        ) : null}

        {hasCustomerStories ? (
          <Box className={`${baseStyles.gridFrameTop} ${baseStyles.gridFrameBottom}`}>
            <Grid columnGap="none" rowGap="none" enableGutters={false}>
              <Grid.Column span={{xsmall: 12, medium: 10, xlarge: 12}} start={{medium: 2}}>
                <Grid columnGap="none" rowGap="none" enableGutters={false} className={baseStyles.narrowStackFrame}>
                  {outroCustomerStories?.map(story => {
                    const Octicon = getPrimerIcon(story.fields.icon)
                    return (
                      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
                      <Grid.Column key={story.sys.id} span={{xsmall: 12, xlarge: 4}} className="gridColumn">
                        <Box
                          className={clsx(baseStyles.gridItem, baseStyles.stackedCardGridItem)}
                          padding={{narrow: 24, regular: 40, wide: 64}}
                          paddingInlineStart={{narrow: 24, regular: 80, wide: 64}}
                          paddingInlineEnd={{narrow: 24, regular: 80, wide: 64}}
                          paddingBlockStart={{regular: 80}}
                          paddingBlockEnd={{regular: 80}}
                          borderWidth="thin"
                          borderColor="muted"
                          style={{height: '100%'}}
                        >
                          <Card
                            href={story.fields.href}
                            variant="minimal"
                            fullWidth
                            ctaVariant="arrow"
                            ctaText={story.fields.label}
                          >
                            {Octicon ? <Card.Icon icon={<Octicon />} color="default" hasBackground /> : null}
                            {story.fields.eyebrow ? <Card.Label>{story.fields.eyebrow}</Card.Label> : null}
                            <Card.Heading>
                              {renderWithoutParagraphs(story.fields.heading)}
                              {story.fields.alt ? <span className="sr-only"> {story.fields.alt}</span> : null}
                            </Card.Heading>
                            <Card.Description>{renderWithoutParagraphs(story.fields.description)}</Card.Description>
                          </Card>
                        </Box>
                      </Grid.Column>
                    )
                  })}
                </Grid>
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {hasCtaBanner ? (
          <Box paddingBlockStart={{narrow: 'spacious', regular: 64}}>
            <FlexSuiteOutroCTA
              outroCtaHeading={outroCtaHeading}
              outroCtaDescription={outroCtaDescription}
              outroCtaOne={outroCtaOne}
              outroCtaTwo={outroCtaTwo}
            />
          </Box>
        ) : null}

        {hasBrandedDivider ? (
          <Box className={baseStyles.gridFrameBottom} data-testid="category-outro-branded-divider">
            <Grid>
              <Grid.Column>
                <BrandedDivider brand={brand} />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {hasResources ? (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        ) : null}
      </Section>
    </Box>
  )
}
